import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";
import NotificationTemplateCard from "./components/NotificationTemplateCard";
import { fetchNotificationTemplates } from "./services/notificationTemplates";
import { NotificationTemplate } from "./types/types";

export default function NotificationManager() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    setLoading(true);
    fetchNotificationTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = search.trim() === "" || t.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && t.isActive) ||
      (statusFilter === "Inactive" && !t.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: 5, bgcolor: colors.primary[900], minHeight: "100vh", borderRadius: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary[700], fontSize: "1.75rem" }}>
          Notification Manager
        </Typography>
        <Typography variant="body2" sx={{ color: colors.primary[300], mt: 0.5 }}>
          Manage the notification templates fired by backend events.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          placeholder="Search here"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            bgcolor: colors.primary[900],
            borderRadius: 8,
            width: 280,
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{
            bgcolor: colors.primary[900],
            borderRadius: 8,
            width: 140,
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          }}
        >
          <MenuItem value="All">Status</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </TextField>
      </Box>

      <Grid container spacing={3}>
        {loading ? (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary[400] }} />
            </Box>
          </Grid>
        ) : filteredTemplates.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ color: colors.primary[300], textAlign: "center", py: 6 }}>
              No notification templates found.
            </Typography>
          </Grid>
        ) : (
          filteredTemplates.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template.id}>
              <NotificationTemplateCard
                template={template}
                colors={colors}
                onClick={() => navigate(`/configurations/notification-manager/edit/${template.id}`)}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
}
